import {
  AutomationTemplateSegmentTypeEnum,
  type AutomationTemplateContents,
} from '@reach/shared-types/automation-template';
import {
  BusinessAutomationActionTypeEnum,
  BusinessAutomationTriggerTypeEnum,
} from '@reach/shared-types/business-automation';
import {
  AUDIENCE_AND_EXCLUSION_PROBLEM,
  EXCLUDED_ELSEWHERE_PREFIX,
  EXCLUDED_ELSEWHERE_SUFFIX,
  SEGMENT_USAGE_PHRASES,
  SEGMENT_USAGE_TAGS,
} from './segment-picker-copy';

export type SegmentUsage =
  | { kind: 'trigger' }
  | { kind: 'audience' }
  | { kind: 'exclusion' }
  | { kind: 'checkAudience'; actionIndex: number };

export type SegmentUsageMap = Map<string, SegmentUsage[]>;

type SegmentDefaults = AutomationTemplateContents['includeSegmentDefaults'];

/** Segment definition IDs in an include/exclude defaults list, without the All Users sentinel. */
export function getSegmentDefaultIds(defaults: SegmentDefaults | undefined): string[] {
  return (defaults ?? [])
    .filter(segment => segment !== AutomationTemplateSegmentTypeEnum.ALL_USERS)
    .map(segment => (typeof segment === 'string' ? segment : segment.id));
}

/** Every place a segment definition is used in a template, keyed by canonical version ID. */
export function getTemplateSegmentUsage(contents: AutomationTemplateContents): SegmentUsageMap {
  const usage: SegmentUsageMap = new Map();
  const add = (segmentId: string, entry: SegmentUsage) => {
    if (!segmentId || segmentId === AutomationTemplateSegmentTypeEnum.ALL_USERS) return;
    usage.set(segmentId, [...(usage.get(segmentId) ?? []), entry]);
  };

  const { triggerMetadata } = contents;
  if (triggerMetadata?.triggerType === BusinessAutomationTriggerTypeEnum.TRIGGER_BASED) {
    add(triggerMetadata.eventFilter?.segment_id, { kind: 'trigger' });
  }
  getSegmentDefaultIds(contents.includeSegmentDefaults).forEach(id => add(id, { kind: 'audience' }));
  getSegmentDefaultIds(contents.excludeSegmentDefaults).forEach(id => add(id, { kind: 'exclusion' }));
  (contents.actionData ?? []).forEach((action, actionIndex) => {
    if (action.type !== BusinessAutomationActionTypeEnum.CHECK_SEGMENT_MEMBERSHIP) return;
    action.segmentIds.forEach(id => add(id, { kind: 'checkAudience', actionIndex }));
  });

  return usage;
}

/** Drops the usages a picker is currently editing, leaving only usages elsewhere in the template. */
export function omitSegmentUsages(
  usage: SegmentUsageMap,
  isBeingEdited: (entry: SegmentUsage) => boolean,
): SegmentUsageMap {
  const remaining: SegmentUsageMap = new Map();
  for (const [segmentId, entries] of usage) {
    const kept = entries.filter(entry => !isBeingEdited(entry));
    const isUsedElsewhere = kept.length > 0;
    if (isUsedElsewhere) remaining.set(segmentId, kept);
  }
  return remaining;
}

const isDefaultsListUsage = (entry: SegmentUsage) =>
  entry.kind === 'audience' || entry.kind === 'exclusion';

/** True when a segment is in both the audience and exclusion lists, which the backend rejects. */
export function hasRejectedSegmentReuse(usage: SegmentUsageMap): boolean {
  return [...usage.values()].some(entries => entries.filter(isDefaultsListUsage).length > 1);
}

/** Warning for a segment whose exclusion overlaps another usage, or null when its usages are fine. */
export function describeSegmentReuseProblem(segmentName: string, usages: SegmentUsage[]): string | null {
  const isExcluded = usages.some(entry => entry.kind === 'exclusion');
  if (!isExcluded) return null;
  const isAlsoAudience = usages.some(entry => entry.kind === 'audience');
  if (isAlsoAudience) return `${segmentName} ${AUDIENCE_AND_EXCLUSION_PROBLEM}`;
  const otherUsages = usages.filter(entry => entry.kind !== 'exclusion');
  if (otherUsages.length === 0) return null;
  return `${segmentName} ${EXCLUDED_ELSEWHERE_PREFIX} ${describeSegmentUsages(otherUsages)}${EXCLUDED_ELSEWHERE_SUFFIX}`;
}

/** Short tags for a segment's other usages, shown next to its name in pickers. */
export function getSegmentUsageTags(entries: SegmentUsage[] | undefined): string[] {
  return [...new Set((entries ?? []).map(entry => SEGMENT_USAGE_TAGS[entry.kind]))];
}

/** Phrase such as "as the trigger and in a Check Audience step", used in duplicate warnings. */
export function describeSegmentUsages(entries: SegmentUsage[]): string {
  const phrases = [...new Set(entries.map(entry => SEGMENT_USAGE_PHRASES[entry.kind]))];
  return phrases.join(' and ');
}

/** Usages edited from the trigger step: the trigger segment, the audience and the exclusions. */
export function isTriggerStepUsage(usage: SegmentUsage): boolean {
  return usage.kind !== 'checkAudience';
}

/** Builds a predicate matching the usage owned by the Check Audience step at `actionIndex`. */
export function isCheckAudienceUsageAt(actionIndex: number): (usage: SegmentUsage) => boolean {
  return usage => usage.kind === 'checkAudience' && usage.actionIndex === actionIndex;
}
